import React, { useState, useEffect, useCallback } from 'react';
import { Settings, FileText, ChevronRight, ChevronDown, BookOpen, Plus, X, CalendarClock, Gauge, History } from 'lucide-react';
import Aval360Questionario from './Aval360Questionario';
import Aval360Versoes from './Aval360Versoes';
import Aval360Historico from './Aval360Historico';
import BsvHistorico from '../bsv/BsvHistorico';
import useEhCompacto from '../../../utils/useEhCompacto';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
import { avisoCicloBimestral } from './cicloBimestral';

// =====================================================================
// Sub-hub "Avaliação 360" (Módulo Operacional).
//
// Clone estrutural do sub-hub da Avaliação BSV (que a 360 substitui) — a tela
// inicial é a CONSULTA (Aval360Historico), e os botões de ação vivem no cabeçalho
// dela: "Nova Avaliação" (pop-up com os formulários publicados + manual) e
// "Gerenciar versões" (só Operacional/Admin).
//
// A 360 difere do BSV no MÉTODO (contagem pura SIM÷(SIM+NÃO), régua Verde/Amarelo/
// Laranja/Vermelho), na EVIDÊNCIA (foto + observação obrigatórias no "Não") e no
// N/A por ÁREA (segmento inexistente). A cor é a ARDÓSIA do módulo (#475569).
// =====================================================================

const API_BASE = '/api/operacional/aval360';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Ponteiro local do rascunho em andamento (gravado pelo autosave do questionário).
// Alimenta o banner "Continuar avaliação em andamento". Mesma chave do Aval360Questionario.
const LS_RASCUNHO_360 = 'aval360_rascunho';

const OperacionalAval360 = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'operacional') || '';
  const podeGerenciarVersoes = ['Admin', 'Operacional'].includes(perfil);
  const podePreencher = ['Admin', 'Operacional', 'Diretoria'].includes(perfil);

  const [aba, setAba] = useState('360'); // aba do card: '360' (ativa) | 'bsv' (histórico só leitura)
  const [tela, setTela] = useState('lista');
  const [versaoSelecionada, setVersaoSelecionada] = useState(null);
  const [rascunhoSel, setRascunhoSel] = useState(null);            // id do rascunho a retomar (ou null = novo)
  const [rascunhoPendente, setRascunhoPendente] = useState(null);  // ponteiro local p/ o banner
  const [modalNovo, setModalNovo] = useState(false);               // pop-up "Nova Avaliação"
  const [versoes, setVersoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  const carregarVersoes = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const r = await fetch(`${API_BASE}/versoes-publicadas`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os formulários publicados.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirNovo = () => { setModalNovo(true); carregarVersoes(); };

  const escolherVersao = (v) => {
    setVersaoSelecionada(v); setRascunhoSel(null); setModalNovo(false); setTela('preencher');
  };

  useEffect(() => {
    if (tela !== 'lista') return;
    try {
      const raw = localStorage.getItem(LS_RASCUNHO_360);
      setRascunhoPendente(raw ? JSON.parse(raw) : null);
    } catch { setRascunhoPendente(null); }
  }, [tela]);

  const continuarRascunhoPendente = () => {
    if (!rascunhoPendente) return;
    setVersaoSelecionada({ ID: rascunhoPendente.idVersao });
    setRascunhoSel(rascunhoPendente.id);
    setTela('preencher');
  };
  const dispensarRascunhoPendente = () => {
    try { localStorage.removeItem(LS_RASCUNHO_360); } catch { /* ignora */ }
    setRascunhoPendente(null);
  };

  if (tela === 'preencher' && versaoSelecionada) {
    return (
      <Aval360Questionario
        idVersao={versaoSelecionada.ID}
        idRascunho={rascunhoSel}
        onVoltar={() => { setTela('lista'); setVersaoSelecionada(null); setRascunhoSel(null); }}
      />
    );
  }

  if (tela === 'versoes') return <Aval360Versoes onVoltar={() => setTela('lista')} />;

  const acoes = (
    <>
      {podePreencher && (
        <button onClick={abrirNovo} style={btnPrimario}>
          <Plus size={16} /> Nova Avaliação
        </button>
      )}
      {podeGerenciarVersoes && (
        <button onClick={() => setTela('versoes')} style={btnSecundario}>
          <Settings size={16} /> Gerenciar versões
        </button>
      )}
    </>
  );

  // Aviso (não-trava) do ciclo bimestral.
  const aviso = avisoCicloBimestral();

  return (
    <>
      {/* Abas do card: a Avaliação 360 (ativa) e o Histórico BSV (só leitura). */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18, borderBottom: '1px solid #e2e8f0' }}>
        <button onClick={() => setAba('360')} style={estiloAba(aba === '360')}>
          <Gauge size={15} /> Avaliação 360
        </button>
        <button onClick={() => setAba('bsv')} style={estiloAba(aba === 'bsv')}>
          <History size={15} /> Histórico BSV
        </button>
      </div>

      {aba === 'bsv' ? (
        <>
          {/* Aviso: instrumento mudou; a série antiga usa a régua/vocabulário ANTIGOS. */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
            border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 12, padding: '12px 14px', marginBottom: 16,
          }}>
            <History size={18} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 13, color: '#92400e' }}>
              <strong>A Avaliação BSV foi substituída pela Avaliação 360.</strong> Este histórico é{' '}
              <strong>somente leitura</strong> — a régua e o vocabulário aqui
              (Excelente/Satisfatório/Atenção/Crítico) são os <strong>ANTIGOS</strong>; a 360 usa a
              régua nova (Verde/Amarelo/Laranja/Vermelho). As 11 avaliações e seus PDFs seguem acessíveis.
            </div>
          </div>
          <BsvHistorico />
        </>
      ) : (
      <>
      {/* Aviso de ciclo bimestral: informativo, NÃO bloqueia o preenchimento
          (decisão DEM-00011 — sem UNIQUE por loja/bimestre). */}
      {podePreencher && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 12, padding: '10px 14px', marginBottom: 16,
        }}>
          <CalendarClock size={18} color="#475569" />
          <div style={{ fontSize: 13, color: '#334155' }}>
            <strong>Ciclo bimestral.</strong> {aviso.texto}
          </div>
        </div>
      )}

      {/* Banner: avaliação em andamento (gravada pelo autosave). */}
      {rascunhoPendente && podePreencher && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
          border: '1px solid #cbd5e1', background: '#f1f5f9', borderRadius: 12, padding: 14, marginBottom: 16,
        }}>
          <FileText size={20} color="#475569" />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 800, color: '#334155' }}>Avaliação em andamento</div>
            <div style={{ fontSize: 13, color: '#475569' }}>
              {rascunhoPendente.versao || 'Avaliação 360'}{rascunhoPendente.loja ? ` · ${rascunhoPendente.loja}` : ''} — salva automaticamente. Continue de onde parou.
            </div>
          </div>
          <button onClick={continuarRascunhoPendente} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px',
            background: '#475569', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
          }}>
            <ChevronRight size={16} /> Continuar
          </button>
          <button onClick={dispensarRascunhoPendente} title="Esconde o aviso; o rascunho continua salvo" style={{
            padding: '8px 12px', background: 'transparent', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}>
            Dispensar
          </button>
        </div>
      )}

      <Aval360Historico acoesExtras={acoes} />

      {modalNovo && (
        <ModalNovaAvaliacao
          versoes={versoes} loading={loading} erro={erro}
          podeGerenciarVersoes={podeGerenciarVersoes}
          onEscolher={escolherVersao} onFechar={() => setModalNovo(false)}
        />
      )}
      </>
      )}
    </>
  );
};

// Botão de aba do card (Avaliação 360 × Histórico BSV). A ativa ganha a cor do
// módulo e uma borda inferior; a inativa fica apagada.
const estiloAba = (ativa) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px',
  background: 'transparent', border: 'none', borderBottom: `3px solid ${ativa ? '#475569' : 'transparent'}`,
  color: ativa ? '#0f172a' : '#94a3b8', fontWeight: 800, fontSize: 14, cursor: 'pointer', marginBottom: -1,
});

// ---------------------------------------------------------------------
// Pop-up "Nova Avaliação": formulários publicados (clicar = iniciar) + manual.
// ---------------------------------------------------------------------
const ModalNovaAvaliacao = ({ versoes, loading, erro, podeGerenciarVersoes, onEscolher, onFechar }) => {
  const compacto = useEhCompacto();
  const [manualAberto, setManualAberto] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: compacto ? 10 : 24, overflowY: 'auto',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, marginTop: compacto ? 8 : 24,
          boxShadow: '0 20px 50px rgba(15,23,42,.25)', overflow: 'hidden',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: compacto ? '14px 16px' : '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ padding: 10, background: '#e2e8f0', borderRadius: 10 }}><Plus size={20} color="#334155" /></div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Nova Avaliação 360</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Escolha o formulário publicado para iniciar a visita.</p>
          </div>
          <button onClick={onFechar} title="Fechar" style={{
            display: 'inline-flex', padding: 8, background: '#f1f5f9', color: '#475569',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: compacto ? 14 : 22 }}>
          <div style={{ border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            <button onClick={() => setManualAberto((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#334155', fontSize: 14,
            }}>
              <BookOpen size={18} /> Como funciona / criar um novo formulário
              <ChevronDown size={18} style={{ marginLeft: 'auto', transform: manualAberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {manualAberto && (
              <div style={{ padding: '0 18px 16px', fontSize: 13, color: '#334155', lineHeight: 1.65 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li><strong>Monte o formulário</strong> — em <strong>Gerenciar versões → Nova versão</strong>: dê um nome, crie as <strong>áreas</strong> e, dentro de cada uma, as <strong>perguntas</strong>. A 360 <strong>não tem peso</strong> — cada pergunta conta 1. Áreas de segmento (Quiosque/Delivery/Drive) podem ser marcadas <strong>N/A inteiras</strong> na loja que não as tem. Por fim, <strong>publique</strong>.</li>
                  <li style={{ marginTop: 6 }}><strong>Faça a avaliação</strong> — clique no formulário publicado: informe <strong>loja, data, avaliador</strong> e os gerentes, e responda cada pergunta como <strong>Sim / Não / N/A</strong>. Marcar <strong>“Não” exige uma foto E uma observação</strong> da evidência. O percentual aparece ao vivo, por área e no total.</li>
                  <li style={{ marginTop: 6 }}><strong>Saia e volte quando quiser</strong> — o preenchimento é <strong>salvo automaticamente</strong>; o aviso <strong>“Avaliação em andamento”</strong> retoma de onde você parou.</li>
                  <li style={{ marginTop: 6 }}><strong>Finalize e consulte</strong> — ao <strong>Finalizar</strong>, o sistema calcula a nota e gera o <strong>PDF</strong>. A avaliação passa a aparecer em <strong>Avaliações gravadas</strong>, com o percentual de cada área e exportação em <strong>PDF</strong> ou <strong>Excel</strong>.</li>
                </ol>
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8 }}>
                  <strong style={{ color: '#334155' }}>Como o resultado é decidido</strong><br />
                  Percentual = perguntas <strong>“Sim”</strong> ÷ (<strong>Sim + Não</strong>) × 100 — contagem simples, sem peso. Pergunta (ou área) marcada <strong>N/A sai do denominador</strong> — não conta nem a favor nem contra.<br />
                  Classificação: ≥90% <strong>Verde</strong> · 80–89,99% <strong>Amarelo</strong> · 70–79,99% <strong>Laranja</strong> · &lt;70% <strong>Vermelho</strong>.<br />
                  <em>Quanto <strong>maior</strong> o percentual, melhor.</em>
                </div>
              </div>
            )}
          </div>

          {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

          {loading ? <p style={{ color: '#64748b' }}>Carregando…</p> : versoes.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              Nenhum formulário publicado.{podeGerenciarVersoes && ' Use "Gerenciar versões" para publicar um.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {versoes.map((v) => (
                <div key={v.ID}
                  onClick={() => onEscolher(v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                  <div style={{ padding: 12, background: '#e2e8f0', borderRadius: 10 }}><FileText size={22} color="#334155" /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</div>
                    {v.DESCRICAO && <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>{v.DESCRICAO}</div>}
                    <div style={{ marginTop: 6, fontSize: 12, color: '#94a3b8' }}>Total de perguntas: {v.TOTAL_PERGUNTAS}</div>
                  </div>
                  <ChevronRight size={20} color="#cbd5e1" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#475569', color: '#fff', border: '1px solid #475569', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};
const btnSecundario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
};

export default OperacionalAval360;
